import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api, setMemberToken } from "../lib/api";

export default function MemberLogin() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/member/login", { email: email.trim().toLowerCase() });
      setMemberToken(res.data.token);
      navigate("/member-dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail || "We couldn't sign you in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-paper min-h-screen flex flex-col" data-testid="member-login-page">
      <Navbar />

      <main className="flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid md:grid-cols-2 gap-16 items-center py-28">
          <div>
            <p className="font-sans text-xs tracking-[0.24em] uppercase text-trailride font-semibold mb-5">
              Members &amp; Officers
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-black tracking-tight text-ink leading-none">
              Member access.
            </h1>
            <p className="font-sans text-ink/60 mt-6 max-w-md leading-relaxed">
              Create your membership with your LSU email — no password needed. You'll land on your
              dashboard, where you can pay annual dues and unlock full access.
            </p>
          </div>

          <div className="w-full max-w-md">
            <form onSubmit={submit} className="space-y-6" data-testid="member-login-form">
              <div>
                <label htmlFor="lsu-email" className="font-sans text-sm text-ink/60">LSU email</label>
                <input
                  id="lsu-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="member-email-input"
                  placeholder="you@lsu.edu"
                  className="w-full bg-transparent border-b border-ink/20 py-3 text-ink text-lg focus:outline-none focus:border-trailride transition-colors placeholder:text-ink/40"
                />
              </div>

              {error && (
                <p className="font-sans text-sm text-red-600" data-testid="member-login-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="member-login-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-trailride text-white font-sans font-medium hover:bg-trailride-dark transition-colors disabled:opacity-60"
              >
                {loading ? "Signing in…" : "Enter"} <ArrowRight size={18} />
              </button>
            </form>

            <div className="mt-8 border-t border-ink/10 pt-6 space-y-3">
              <p className="font-sans text-sm text-ink/60">
                <span className="text-ink font-medium">New to Tiger Trailride?</span> Just enter your LSU email above to
                create your membership account — then pay your annual dues to unlock full member access.
              </p>
              <p className="font-sans text-sm text-ink/60">
                Interested in helping lead?{" "}
                <Link to="/get-involved" data-testid="member-join-link" className="text-trailride font-medium hover:underline underline-offset-4">
                  Explore Executive Board opportunities
                </Link>.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
